/**
 * Utilitaires pour le formatage sécurisé des dates
 */

/**
 * Formate une date de manière sécurisée
 */
export function formatDate(dateString: string | null | undefined, locale: string = 'fr-FR'): string {
  try {
    if (!dateString) {
      return 'Date non définie';
    }

    // Normaliser le format de date Django (YYYY-MM-DD HH:mm:ss)
    let normalizedDate = dateString;
    
    // Si la date est au format Django sans timezone, ajouter 'Z' pour UTC
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      normalizedDate = dateString.replace(' ', 'T') + 'Z';
    }
    // Si c'est juste une date sans heure, ajouter l'heure
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      normalizedDate = dateString + 'T00:00:00Z';
    }

    const date = new Date(normalizedDate);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      console.warn('Date invalide:', dateString, 'normalisée en:', normalizedDate);
      return 'Date invalide';
    }

    return date.toLocaleDateString(locale);
  } catch (error) {
    console.error('Erreur lors du formatage de la date:', error, 'Date originale:', dateString);
    return 'Erreur de format';
  }
}

/**
 * Formate une date avec l'heure de manière sécurisée
 */
export function formatDateTime(dateString: string | null | undefined, locale: string = 'fr-FR'): string {
  try {
    if (!dateString) {
      return 'Date non définie';
    }

    // Normaliser le format de date Django
    let normalizedDate = dateString;
    
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      normalizedDate = dateString.replace(' ', 'T') + 'Z';
    }
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      normalizedDate = dateString + 'T00:00:00Z';
    }

    const date = new Date(normalizedDate);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      return 'Date invalide';
    }

    return date.toLocaleString(locale);
  } catch (error) {
    console.error('Erreur lors du formatage de la date/heure:', error);
    return 'Erreur de format';
  }
}

/**
 * Formate une plage de dates
 */
export function formatDateRange(
  startDate: string | null | undefined, 
  endDate: string | null | undefined, 
  locale: string = 'fr-FR'
): string {
  try {
    if (!startDate || !endDate) {
      return 'Dates non définies';
    }

    // Normaliser les formats de date Django
    let normalizedStart = startDate;
    let normalizedEnd = endDate;
    
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(startDate)) {
      normalizedStart = startDate.replace(' ', 'T') + 'Z';
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(endDate)) {
      normalizedEnd = endDate.replace(' ', 'T') + 'Z';
    }

    const start = new Date(normalizedStart);
    const end = new Date(normalizedEnd);
    
    // Vérifier si les dates sont valides
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Dates invalides';
    }

    const startFormatted = start.toLocaleDateString(locale);
    const endFormatted = end.toLocaleDateString(locale);
    
    return `Du ${startFormatted} au ${endFormatted}`;
  } catch (error) {
    console.error('Erreur lors du formatage de la plage de dates:', error);
    return 'Erreur de format de dates';
  }
}

/**
 * Vérifie si une date est valide
 */
export function isValidDate(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Convertit une date pour les inputs datetime-local
 */
export function toDateTimeLocalString(dateString: string | null | undefined): string {
  try {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // Format YYYY-MM-DDTHH:mm pour datetime-local
    return date.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}
